import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const ExpandedMessageModal = ({ 
  isOpen, 
  message, 
  onClose, 
  darkMode = false 
}) => {
  // Close modal on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen || !message) return null;

  const modalContent = (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Enhanced Backdrop with gradient */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/60 to-black/50 backdrop-blur-md"
          onClick={onClose}
        />
        
        {/* Enhanced Modal with glow effects */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 40, rotateX: 5 }}
          animate={{ opacity: 1, scale: 1, y: 0, rotateX: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 40, rotateX: 5 }}
          transition={{ 
            duration: 0.4, 
            ease: [0.16, 1, 0.3, 1],
            type: "spring",
            stiffness: 300,
            damping: 30
          }}
          className={`
            relative w-full max-w-4xl max-h-[90vh] overflow-hidden
            rounded-3xl shadow-2xl border backdrop-blur-xl
            ${darkMode 
              ? 'bg-gray-900/95 border-gray-600 shadow-purple-500/20' 
              : 'bg-white/95 border-gray-300 shadow-purple-500/10'
            }
            before:absolute before:inset-0 before:rounded-3xl before:p-[1px]
            before:bg-gradient-to-br before:from-purple-400/30 before:via-pink-400/30 before:to-purple-400/30
            before:-z-10
          `}
        >
          {/* Chat Sidebar Style Header */}
          <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 relative overflow-hidden">
            <div className="flex items-center justify-between relative z-10">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center backdrop-blur-sm">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h2 className="font-bold text-lg text-white">
                    SEO Consultant
                  </h2>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                    <span className="text-sm text-white text-opacity-90">
                      {message.type === 'user' ? 'Your Message' : 'Detailed Response'}
                    </span>
                    {message.timestamp && (
                      <>
                        <span className="text-white text-opacity-50">•</span>
                        <span className="text-xs text-white text-opacity-75">
                          {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
              
              <motion.button
                onClick={onClose}
                className="p-2 rounded-lg transition-colors hover:bg-white hover:bg-opacity-20 text-white hover:text-white"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                transition={{ type: "spring", stiffness: 300, damping: 20 }}
                title="Close"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </motion.button>
            </div>
          </div>

          {/* Clean Content Area */}
          <div className={`overflow-y-auto max-h-[calc(90vh-120px)] scrollbar-thin scrollbar-track-transparent ${
            darkMode 
              ? 'bg-gray-800 scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-500' 
              : 'bg-white scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400'
          }`}>
            <motion.div 
              className="p-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              {/* Tools used indicator */}
              {message.toolsUsed && (
                <div className={`mb-4 p-3 rounded-lg border-l-4 ${
                  darkMode 
                    ? 'bg-purple-900/20 border-purple-500 text-purple-200' 
                    : 'bg-purple-50 border-purple-400 text-purple-800'
                }`}>
                  <div className="flex items-center gap-2 text-sm font-medium mb-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    Tools Used
                  </div>
                  <div className="text-sm">
                    {message.toolsUsed.join(', ')}
                  </div>
                </div>
              )}

              {/* Context indicator */}
              {message.contextDetected && (
                <div className={`mb-4 p-3 rounded-lg border-l-4 ${
                  darkMode 
                    ? 'bg-blue-900/20 border-blue-500 text-blue-200' 
                    : 'bg-blue-50 border-blue-400 text-blue-800'
                }`}>
                  <div className="flex items-center gap-2 text-sm font-medium mb-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />
                    </svg>
                    Page Context
                  </div>
                  <div className="text-sm">
                    📍 {message.contextDetected}
                  </div>
                </div>
              )}

              {/* Message content with responsive styling */}
              <div className={`prose prose-sm max-w-none ${
                darkMode 
                  ? 'prose-invert prose-headings:text-gray-100 prose-p:text-gray-200 prose-strong:text-gray-100 prose-em:text-gray-300 prose-code:text-gray-200 prose-code:bg-gray-700 prose-pre:bg-gray-700 prose-blockquote:text-gray-300 prose-blockquote:border-gray-600' 
                  : 'prose-headings:text-gray-900 prose-p:text-gray-800 prose-strong:text-gray-900 prose-em:text-gray-700 prose-code:text-gray-800 prose-code:bg-gray-100 prose-pre:bg-gray-50 prose-blockquote:text-gray-700 prose-blockquote:border-gray-300'
              }`}>
                <ReactMarkdown 
                  remarkPlugins={[remarkGfm]}
                  components={{
                    // Custom components for better styling (same as chat components)
                    h1: ({ children }) => <h1 className={`text-xl font-bold mb-4 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{children}</h1>,
                    h2: ({ children }) => <h2 className={`text-lg font-bold mb-3 ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{children}</h2>,
                    h3: ({ children }) => <h3 className={`text-base font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{children}</h3>,
                    p: ({ children }) => <p className={`mb-4 last:mb-0 leading-relaxed ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{children}</p>,
                    ul: ({ children }) => <ul className={`mb-4 pl-6 space-y-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{children}</ul>,
                    ol: ({ children }) => <ol className={`mb-4 pl-6 space-y-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{children}</ol>,
                    li: ({ children }) => <li className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>{children}</li>,
                    code: ({ children, className }) => {
                      const isInline = !className;
                      return isInline ? (
                        <code className={`px-1 py-0.5 rounded text-xs font-mono ${
                          darkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {children}
                        </code>
                      ) : (
                        <code className={`block p-3 rounded text-sm font-mono overflow-x-auto ${
                          darkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-50 text-gray-800'
                        }`}>
                          {children}
                        </code>
                      );
                    },
                    blockquote: ({ children }) => (
                      <blockquote className={`border-l-4 pl-4 py-2 my-4 italic ${
                        darkMode ? 'border-gray-600 text-gray-300' : 'border-gray-300 text-gray-700'
                      }`}>
                        {children}
                      </blockquote>
                    ),
                    strong: ({ children }) => <strong className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>{children}</strong>,
                    em: ({ children }) => <em className={`italic ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{children}</em>,
                    // Table components for data visualization
                    table: ({ children }) => (
                      <div className="overflow-x-auto my-6 rounded-lg shadow-sm">
                        <table className={`min-w-full border-collapse text-sm ${
                          darkMode ? 'bg-gray-800' : 'bg-white'
                        }`}>
                          {children}
                        </table>
                      </div>
                    ),
                    thead: ({ children }) => (
                      <thead className={
                        darkMode 
                          ? 'bg-gradient-to-r from-blue-900 to-blue-800' 
                          : 'bg-gradient-to-r from-blue-50 to-blue-100'
                      }>
                        {children}
                      </thead>
                    ),
                    tbody: ({ children }) => <tbody className="divide-y divide-gray-200 dark:divide-gray-700">{children}</tbody>,
                    tr: ({ children }) => (
                      <tr className={`transition-colors duration-150 ${
                        darkMode 
                          ? 'hover:bg-gray-750' 
                          : 'hover:bg-blue-25 even:bg-blue-25/50'
                      }`}>
                        {children}
                      </tr>
                    ),
                    th: ({ children }) => (
                      <th className={`px-4 py-3 text-left text-sm font-semibold tracking-wider uppercase ${
                        darkMode 
                          ? 'text-blue-200 border-b border-blue-700' 
                          : 'text-blue-900 border-b border-blue-200'
                      }`}>
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className={`px-4 py-3 text-sm whitespace-nowrap ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {children}
                      </td>
                    ),
                    // Enhanced markdown elements
                    input: ({ type, checked, ...props }) => {
                      if (type === 'checkbox') {
                        return (
                          <input
                            type="checkbox"
                            checked={checked}
                            readOnly
                            className={`mr-3 rounded border-2 ${
                              darkMode 
                                ? 'border-gray-600 bg-gray-700 text-blue-400' 
                                : 'border-gray-300 bg-white text-blue-600'
                            } focus:ring-2 focus:ring-blue-500`}
                            {...props}
                          />
                        );
                      }
                      return <input {...props} />;
                    },
                    // Custom components for enhanced styling
                    span: ({ children, className, ...props }) => {
                      // Badge styling
                      if (className?.includes('badge')) {
                        return (
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                            darkMode 
                              ? 'bg-blue-900 text-blue-200 border border-blue-700' 
                              : 'bg-blue-100 text-blue-800 border border-blue-200'
                          }`} {...props}>
                            {children}
                          </span>
                        );
                      }
                      // Alert/callout styling
                      if (className?.includes('alert')) {
                        const alertType = className.split('-')[1] || 'info';
                        const alertStyles = {
                          info: darkMode 
                            ? 'bg-blue-900/50 border-blue-700 text-blue-200' 
                            : 'bg-blue-50 border-blue-200 text-blue-800',
                          warning: darkMode 
                            ? 'bg-yellow-900/50 border-yellow-700 text-yellow-200' 
                            : 'bg-yellow-50 border-yellow-200 text-yellow-800',
                          success: darkMode 
                            ? 'bg-green-900/50 border-green-700 text-green-200' 
                            : 'bg-green-50 border-green-200 text-green-800',
                          error: darkMode 
                            ? 'bg-red-900/50 border-red-700 text-red-200' 
                            : 'bg-red-50 border-red-200 text-red-800'
                        };
                        return (
                          <div className={`border-l-4 p-4 my-4 rounded-r ${alertStyles[alertType] || alertStyles.info}`} {...props}>
                            {children}
                          </div>
                        );
                      }
                      return <span className={className} {...props}>{children}</span>;
                    },
                    // Horizontal rule styling
                    hr: () => (
                      <hr className={`my-6 border-t-2 ${
                        darkMode ? 'border-gray-600' : 'border-gray-200'
                      }`} />
                    ),
                    ul: ({ children }) => (
                      <ul className={`space-y-3 my-6 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {children}
                      </ul>
                    ),
                    ol: ({ children }) => (
                      <ol className={`space-y-3 my-6 ${
                        darkMode ? 'text-gray-300' : 'text-gray-700'
                      }`}>
                        {children}
                      </ol>
                    ),
                    li: ({ children, className, ...props }) => {
                      const isCheckbox = typeof children === 'object' && children?.some?.(child => 
                        child?.type === 'input' && child?.props?.type === 'checkbox'
                      );
                      
                      return (
                        <li 
                          className={`flex items-start gap-4 py-2 ${
                            isCheckbox 
                              ? 'items-center' 
                              : `relative pl-8 ${
                                  darkMode 
                                    ? 'before:absolute before:left-0 before:top-3 before:w-2.5 before:h-2.5 before:bg-blue-400 before:rounded-full before:ring-2 before:ring-blue-400/20' 
                                    : 'before:absolute before:left-0 before:top-3 before:w-2.5 before:h-2.5 before:bg-blue-500 before:rounded-full before:ring-2 before:ring-blue-500/20'
                                }`
                          } ${className || ''}`}
                          {...props}
                        >
                          {isCheckbox ? (
                            <div className="flex items-center gap-3 w-full">
                              {children}
                            </div>
                          ) : (
                            <div className="flex-1 leading-relaxed text-base">{children}</div>
                          )}
                        </li>
                      );
                    },
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            </motion.div>
          </div>

          {/* Enhanced Footer */}
          <div className={`
            p-4 border-t flex justify-end relative overflow-hidden
            ${darkMode 
              ? 'border-gray-700 bg-gradient-to-r from-gray-800 via-gray-800 to-gray-700' 
              : 'border-gray-200 bg-gradient-to-r from-gray-50 via-white to-gray-50'
            }
          `}>
            {/* Subtle glow effect */}
            <div className={`absolute inset-0 opacity-30 ${
              darkMode ? 'bg-blue-900/5' : 'bg-blue-100/20'
            }`} />
            <motion.button
              onClick={onClose}
              className={`
                px-6 py-2 rounded-xl font-medium transition-all duration-200 relative z-10
                ${darkMode 
                  ? 'bg-gradient-to-r from-gray-700 to-gray-600 text-gray-200 hover:from-gray-600 hover:to-gray-500 shadow-lg hover:shadow-xl' 
                  : 'bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 hover:from-gray-200 hover:to-gray-300 shadow-lg hover:shadow-xl'
                }
              `}
              whileHover={{ scale: 1.05, y: -1 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              Close
            </motion.button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );

  return createPortal(modalContent, document.body);
};

export default ExpandedMessageModal;
